import * as joint from '@clientio/rappid';
import { FLOWSHEET_TEMPLATES } from './flowsheet-templates';

const COL_SPACING = 160;
const ROW_SPACING = 120;
const UNIT_STAGGER_MS = 220;
const LINK_STAGGER_MS = 100;

/**
 * Returns a link endpoint using a named port if it exists, otherwise
 * falls back to an anchor on the bounding box edge.
 */
function endpoint(cell, portId, anchorName) {
    if (cell.hasPorts() && cell.getPort(portId)) {
        return { id: cell.id, port: portId };
    }
    return { id: cell.id, anchor: { name: anchorName } };
}

/**
 * Places a predefined flowsheet on the Designer canvas.
 * Units drop in sequentially with animation, then streams are drawn.
 *
 * @param {object} rappid - KitchenSinkService instance
 * @param {string|object} flowsheetKeyOrTemplate - key from FLOWSHEET_TEMPLATES, or an inline template object
 */
export async function buildFlowsheet(rappid, flowsheetKeyOrTemplate) {
    const template = typeof flowsheetKeyOrTemplate === 'object'
        ? flowsheetKeyOrTemplate
        : FLOWSHEET_TEMPLATES[flowsheetKeyOrTemplate];
    if (!template) {
        console.warn(`buildFlowsheet: unknown key "${flowsheetKeyOrTemplate}"`);
        return;
    }

    // Find base position: to the right of existing equipment, or a fixed canvas position
    const elements = rappid.graph.getElements().filter(el => el.attributes.isUnit === 'yes');
    let baseX, baseY;
    if (elements.length > 0) {
        let maxX = -Infinity, sumY = 0;
        elements.forEach(el => {
            const { x, y } = el.position();
            const { width, height } = el.size();
            maxX = Math.max(maxX, x + width);
            sumY += y + height / 2;
        });
        baseX = maxX + 100;
        baseY = sumY / elements.length - ROW_SPACING / 2;
    } else {
        baseX = 100;
        baseY = 100;
    }

    // Place units sequentially with stagger — autoCenter=false to stop canvas jumping
    const cellIds = [];
    for (let i = 0; i < template.units.length; i++) {
        const { type, label, col, row } = template.units[i];
        const x = baseX + col * COL_SPACING;
        const y = baseY + row * ROW_SPACING;

        await new Promise(resolve => setTimeout(resolve, i === 0 ? 0 : UNIT_STAGGER_MS));
        const cellId = rappid.addEquipmentByType(type, x, y, false);
        cellIds.push(cellId);
        if (cellId && label) {
            const cell = rappid.graph.getCell(cellId);
            if (cell) cell.attr('shapename/innerHTML', label);
        }
    }

    // Wait for drop animations to finish, then draw streams
    const totalAnimDuration = template.units.length * UNIT_STAGGER_MS + 1300;
    await new Promise(resolve => setTimeout(resolve, totalAnimDuration));

    for (let i = 0; i < template.connections.length; i++) {
        const [fromIdx, toIdx] = template.connections[i];
        await new Promise(resolve => setTimeout(resolve, LINK_STAGGER_MS));

        const fromUnit = template.units[fromIdx];
        const toUnit   = template.units[toIdx];
        const goingDown = toUnit.row > fromUnit.row;
        const fromCell = cellIds[fromIdx] ? rappid.graph.getCell(cellIds[fromIdx]) : null;
        const toCell   = cellIds[toIdx]   ? rappid.graph.getCell(cellIds[toIdx])   : null;
        if (!fromCell || !toCell) continue;

        const link = new joint.shapes.standard.Link({
            attrs: { line: { stroke: 'black', strokeWidth: 1 } },
            router: { name: 'manhattan', padding: 10, step: 10 },
        });
        link.source(endpoint(fromCell, goingDown ? 'out_bottom_1' : 'out_right_1', goingDown ? 'bottom' : 'right'));
        link.target(endpoint(toCell,   goingDown ? 'in_top_1'    : 'in_left_1',   goingDown ? 'top'    : 'left'));
        rappid.graph.addCell(link);
        if (rappid.graphAdd && rappid.paper) {
            rappid.paper.once('render:done', () => {
                try { rappid.graphAdd(link); } catch {}
            });
        }
    }
}
